'use client'

import React, { createContext, useContext, useEffect, useState } from 'react';
import { colorSystem, getCSSVariables } from './colorSystem';
import { defaultFontSystem, applyFontSystem, type FontSystem } from './fontSystem';

interface ThemeContextType {
  primaryColor: string;
  fontSystem: FontSystem;
  // eslint-disable-next-line no-unused-vars
  setPrimaryColor: (color: string) => void;
  // eslint-disable-next-line no-unused-vars
  applyTheme: (color: string) => void;
  // eslint-disable-next-line no-unused-vars
  setFontSystem: (fontSystem: FontSystem) => void;
  resetTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultPrimaryColor?: string;
}

export function ThemeProvider({ 
  children, 
  defaultPrimaryColor = colorSystem.primary 
}: ThemeProviderProps) {
  const [primaryColor, setPrimaryColor] = useState(defaultPrimaryColor);
  const [fontSystem, setFontSystem] = useState<FontSystem>(defaultFontSystem);

  const applyTheme = (color: string) => {
    setPrimaryColor(color);
    
    // Apply CSS custom properties to document root
    const cssVars = getCSSVariables(color);
    const root = document.documentElement;
    
    Object.entries(cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  };

  const resetTheme = () => {
    applyTheme(colorSystem.primary);
  };

  // Apply theme on mount and when primaryColor or fontSystem changes
  useEffect(() => {
    applyTheme(primaryColor);
    applyFontSystem(fontSystem);
  }, [primaryColor, fontSystem]);

  const value = {
    primaryColor,
    fontSystem,
    setPrimaryColor,
    applyTheme,
    setFontSystem,
    resetTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

